/* ------------------------------------------------------------------
   Honey Biotics PDP behaviours (hb- namespace)
   - <hb-buy-box>: pack selector + subscribe & save + sticky bar sync
   - <hb-gallery>: scroll-snap gallery dots
   - <hb-carousel>: before/after composite carousel (arrows + counter)
   - <hb-video-loader>: click-to-load video (no bytes until tapped)
   The sticky bar itself reuses the theme's <product-sticky-add-to-cart>
   element from theme.js (IntersectionObserver sandwich visibility).
   Safe to include multiple times: definitions are guarded.
------------------------------------------------------------------- */
(function () {
  'use strict';

  if (window.__hbPdpLoaded) return;
  window.__hbPdpLoaded = true;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ============================================================
     BUY BOX
  ============================================================ */
  var HBBuyBox = (function () {
    function findChecked(inputs) {
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].checked) return inputs[i];
      }
      return null;
    }

    return class extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.getAttribute('form-id'));
        if (!this.form) return;

        this.variantInput = this.form.querySelector('[data-hb-variant-input]');
        this.planInput = this.form.querySelector('[data-hb-selling-plan]');
        this.ctaLabel = this.form.querySelector('[data-hb-cta-label]');
        this.paymentWrap = this.form.querySelector('[data-hb-payment]');
        this.packInputs = Array.prototype.slice.call(this.querySelectorAll('.hb-pack input[type="radio"]'));
        this.typeInputs = Array.prototype.slice.call(this.querySelectorAll('.hb-plan input[type="radio"]'));
        this.planSelect = this.querySelector('[data-hb-plan-select]');
        this.planFrequency = this.querySelector('[data-hb-plan-frequency]');
        this.planNote = this.querySelector('[data-hb-plan-note]');

        var update = this.update.bind(this);
        this.packInputs.forEach(function (input) { input.addEventListener('change', update); });
        this.typeInputs.forEach(function (input) { input.addEventListener('change', update); });
        if (this.planSelect) this.planSelect.addEventListener('change', update);

        // Deep link: ?pack=1|3|5 (quiz traffic) preselects a pack card.
        // Anything else — absent, invalid or sold out — keeps the default.
        var packParam = new URLSearchParams(window.location.search).get('pack');
        if (packParam === '1' || packParam === '3' || packParam === '5') {
          var packTarget = this.packInputs.filter(function (input) {
            return input.value === packParam && !input.disabled;
          })[0];
          if (packTarget) packTarget.checked = true;
        }

        this.update();
      }

      get subscribing() {
        var type = findChecked(this.typeInputs);
        return !!(type && type.value === 'subscribe');
      }

      get sellingPlanId() {
        if (this.planSelect) return this.planSelect.value;
        var subscribeInput = this.typeInputs.filter(function (input) { return input.value === 'subscribe'; })[0];
        return subscribeInput ? subscribeInput.dataset.sellingPlan || '' : '';
      }

      update() {
        var subscribing = this.subscribing;

        // Subscriptions only exist on the single tube: snap to pack 1 and lock bundles.
        if (subscribing) {
          var packOne = this.packInputs.filter(function (input) { return input.value === '1'; })[0];
          if (packOne && !packOne.checked) packOne.checked = true;
        }

        this.packInputs.forEach(function (input) {
          var card = input.closest('.hb-pack');
          if (!card) return;
          var locked = subscribing && input.value !== '1';
          card.classList.toggle('hb-pack--locked', locked);
        });

        if (this.planFrequency) this.planFrequency.hidden = !subscribing;
        if (this.planNote) this.planNote.hidden = !subscribing;

        var pack = findChecked(this.packInputs);
        if (!pack) return;

        this.dataset.pack = pack.value;

        if (this.variantInput && pack.dataset.variantId) {
          this.variantInput.value = pack.dataset.variantId;
        }

        if (this.planInput) {
          if (subscribing && this.sellingPlanId) {
            this.planInput.disabled = false;
            this.planInput.value = this.sellingPlanId;
          } else {
            this.planInput.disabled = true;
            this.planInput.value = '';
          }
        }

        if (this.ctaLabel) {
          if (subscribing && pack.dataset.ctaSubLabel) {
            this.ctaLabel.textContent = pack.dataset.ctaSubLabel;
          } else if (pack.dataset.ctaLabel) {
            this.ctaLabel.textContent = pack.dataset.ctaLabel;
          }
        }

        // Dynamic checkout buys product.selected_or_first_available_variant of the
        // single product; only show it when that is exactly what is selected.
        if (this.paymentWrap) {
          this.paymentWrap.hidden = !(pack.value === '1' && !subscribing);
        }

        // Sync the sticky bar price line (if the sticky section is on the page).
        var stickyLabel = pack.dataset.stickyLabel;
        if (stickyLabel) {
          document.querySelectorAll('[data-hb-sticky-price]').forEach(function (element) {
            element.textContent = stickyLabel;
          });
        }
      }
    };
  })();

  if (!window.customElements.get('hb-buy-box')) {
    window.customElements.define('hb-buy-box', HBBuyBox);
  }

  /* ============================================================
     GALLERY (scroll-snap dots)
  ============================================================ */
  var HBGallery = class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hb-gallery__track');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.hb-gallery__slide'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.hb-gallery__dot'));
      if (!this.track || this.slides.length < 2) return;

      var self = this;

      this.dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          var slide = self.slides[index];
          if (!slide) return;
          self.track.scrollTo({
            left: slide.offsetLeft - self.track.offsetLeft,
            behavior: prefersReducedMotion ? 'auto' : 'smooth'
          });
        });
      });

      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var index = self.slides.indexOf(entry.target);
            self.dots.forEach(function (dot, dotIndex) {
              dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
            });
          });
        }, { root: this.track, threshold: 0.6 });

        this.slides.forEach(function (slide) { observer.observe(slide); });
      }
    }
  };

  if (!window.customElements.get('hb-gallery')) {
    window.customElements.define('hb-gallery', HBGallery);
  }

  /* ============================================================
     GENERIC CAROUSEL (before/after composites)
  ============================================================ */
  var HBCarousel = class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hb-ba__track');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.hb-ba__slide'));
      this.counter = this.querySelector('.hb-ba__counter');
      var prev = this.querySelector('[data-hb-prev]');
      var next = this.querySelector('[data-hb-next]');
      if (!this.track || this.slides.length === 0) return;

      var self = this;

      function scrollBySlide(direction) {
        var amount = self.slides[0].getBoundingClientRect().width + 12;
        self.track.scrollBy({
          left: amount * direction,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      }

      if (prev) prev.addEventListener('click', function () { scrollBySlide(-1); });
      if (next) next.addEventListener('click', function () { scrollBySlide(1); });

      if (this.counter && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var index = self.slides.indexOf(entry.target);
            self.counter.textContent = (index + 1) + ' / ' + self.slides.length;
          });
        }, { root: this.track, threshold: 0.6 });

        this.slides.forEach(function (slide) { observer.observe(slide); });
      }
    }
  };

  if (!window.customElements.get('hb-carousel')) {
    window.customElements.define('hb-carousel', HBCarousel);
  }

  /* ============================================================
     CLICK-TO-LOAD VIDEO (no network request until tapped)
  ============================================================ */
  var HBVideoLoader = class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.load.bind(this), { once: false });
      this.addEventListener('keydown', function (event) {
        if (event.code === 'Enter' || event.code === 'Space') {
          event.preventDefault();
          this.load();
        }
      }.bind(this));
    }

    load() {
      if (this.classList.contains('is-playing')) return;
      var src = this.getAttribute('video-src');
      if (!src) return;

      var video = document.createElement('video');
      video.setAttribute('controls', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('preload', 'none');
      video.poster = this.getAttribute('video-poster') || '';
      video.src = src; // the download starts here, only on interaction

      this.prepend(video);
      this.classList.add('is-playing');
      video.play();
    }
  };

  if (!window.customElements.get('hb-video-loader')) {
    window.customElements.define('hb-video-loader', HBVideoLoader);
  }

  /* ============================================================
     REVEAL ON SCROLL (hb- sections only)
     theme.css hides [reveal-on-scroll="true"] (opacity: 0) and each
     stock section animates its own children; hb- sections do it here.
     Mirrors the theme's motion (fade + 24px rise, easeOut-expo-ish).
  ============================================================ */
  function initHbReveals() {
    var selector = [
      '.shopify-section--hb-product-hero [reveal-on-scroll="true"]',
      '.shopify-section--hb-product-story [reveal-on-scroll="true"]'
    ].join(', ');

    var elements = Array.prototype.slice.call(document.querySelectorAll(selector))
      .filter(function (element) { return !element.__hbRevealed; });

    if (elements.length === 0) return;

    function show(element, index) {
      element.__hbRevealed = true;
      if (prefersReducedMotion || typeof element.animate !== 'function') {
        element.style.opacity = '1';
        return;
      }
      element.animate(
        [
          { opacity: 0, transform: 'translateY(24px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ],
        {
          duration: 700,
          delay: Math.min(index, 6) * 90,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both'
        }
      ).finished.then(function () {
        element.style.opacity = '1';
      }).catch(function () {
        element.style.opacity = '1';
      });
    }

    if (!('IntersectionObserver' in window) || prefersReducedMotion) {
      elements.forEach(function (element) { show(element, 0); });
      return;
    }

    var batchIndex = 0;
    var lastBatchTime = 0;
    var observer = new IntersectionObserver(function (entries) {
      var now = performance.now();
      if (now - lastBatchTime > 250) batchIndex = 0;
      lastBatchTime = now;

      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        show(entry.target, batchIndex++);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -10% 0px' });

    elements.forEach(function (element) { observer.observe(element); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHbReveals);
  } else {
    initHbReveals();
  }

  // Re-run when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', initHbReveals);
})();
